import React, { useCallback, useMemo } from 'react'
import { Downgraded, State, useState } from '@hookstate/core'
import { Col, Row } from 'react-bootstrap'
import { Job, useExhibitorDataState } from '../../globalStates/ExhibitorDataState'
import { useLanguageState } from '../../globalStates/LanguageState'
import { ReferenceDataType, withReferenceDataState } from '../../globalStates/ReferenceDataHOC'
import { getElementFieldValue, hasErrorsAll, makeCheckHandler, makeValidationHandler } from '../../globalStates/utils'
import DateField from '../../components/Field/Date'
import TextField from '../../components/Field/Text'
import TextAreaField from '../../components/Field/TextArea'
import ToggleSwitchField from '../../components/Field/ToggleSwitchField'
import FilePicker from '../../components/FilePicker'
import PageContents from '../../components/PageContents'
import Section from '../../components/Section'
import { Tabs, Tab } from '../../components/Tabs'
import RadioField from '../../components/Field/Radio'
import RadioGroup from '../../components/Field/RadioGroup'
import { fieldNameByLang, getElementFieldValueWithLanguage, makeSetHandlerWithLanguage, makeValidationHandlerWithLanguage, useLanguageSwitch } from '../../utils/Language'
import { useConfigDataState } from '../../globalStates/ConfigDataState'
import CategoriesContents from '../categories/CategoriesContents'
import MultimediaContents from '../multimedia/MutimediaContents'
import PersonsSection from '../persons/PersonsSection'
import LanguageSwitch from '../languageSwitch'
import { now } from '../../utils/Dates'

interface JobsSectionProps {
    jobState: State<Job>
    index: number
    onClickRemove: (productId: string) => void
}

const JobsSection: React.FC<JobsSectionProps & ReferenceDataType> = ({ jobState, refDataState, index, onClickRemove }) => {
    const branding = useLanguageState().getStrings()
    const exiState = useExhibitorDataState()
    const job = useState(jobState)
    const isMonoLingual = branding.configuration.monoLingual ? 'De' : 'En'
    const { currentLanguage, onChangeLanguageHandler } = useLanguageSwitch(isMonoLingual);
    const serviceKeys = useConfigDataState().getServiceKeys()
    const refCategories = useMemo(() => refDataState.getCategories("JOB", branding.configuration.jobsPage.sctJobs.categoryWhitelist), [branding.configuration.jobsPage.sctJobs.categoryWhitelist, refDataState])
    const categoryVisible = serviceKeys.category ? serviceKeys.category > 0 && refCategories.length > 0 : false

    const onChangeLogo = useCallback((image: File, onUploadProgress, onSuccessCallback, onErrorCallback) => {
        exiState.upload("LOGO", image, onUploadProgress)
            .then(response => {
                onSuccessCallback();
                if (response) {
                    job.logoFileId.set(response.fileId)
                    job.logoUrl.set(response.fileUrl)
                }
            })
            .catch(reason => onErrorCallback(reason))
    }, [exiState, job])

    const onRemoveLogo = useCallback(() => {
        job.logoFileId.set('')
        job.logoUrl.set('')
    }, [job])

    const onClickRemoveHandler = useCallback(() => {
        onClickRemove(job.id.get())
    }, [onClickRemove, job])

    const onChangeBackgroundImage = useCallback((image: File, onUploadProgress, onSuccessCallback, onErrorCallback) => {
        exiState.upload("BACKGROUND_IMAGE", image, onUploadProgress)
            .then(response => {
                onSuccessCallback();
                if (response) {
                    job.backgroundImageFileId.set(response.fileId)
                    job.backgroundImageUrl.set(response.fileUrl)
                }
            })
            .catch(reason => onErrorCallback(reason))
    }, [exiState, job])

    const onRemoveBackgroundImage = useCallback(() => {
        job.backgroundImageFileId.set('')
        job.backgroundImageUrl.set('')
    }, [job])

    return (
        <Section
            title={job.title.get() || job.titleDe.get() || branding.configuration.jobsPage.sctJobs.titleEmptyJobs}
            collapsible
            onClickRemove={onClickRemoveHandler}
            entityState={job}>
            <Row>
                <Col xs={3}>
                    <FilePicker
                        name="companyLogo"
                        label={branding.configuration.jobsPage.sctJobs.lblJobsImage}
                        message={branding.configuration.jobsPage.sctJobs.lblJobsImageMessage}
                        browseLabel={branding.configuration.jobsPage.sctJobs.btnBrowseFiles}
                        deleteLabel={branding.configuration.jobsPage.sctJobs.btnDeleteImg}
                        placeholderTitle={branding.configuration.jobsPage.sctJobs.hntTitle}
                        placeholderText={branding.configuration.jobsPage.sctJobs.hntProp}
                        value={job.logoUrl.get()}
                        onChangeImage={onChangeLogo}
                        onRemoveImage={onRemoveLogo}
                    />
                </Col>
                <Col xs={9}>
                    <Row>
                        <Col>
                            <LanguageSwitch
                                id={`jobs_${index}_language_switch`}
                                name={`jobs_${index}_languages`}
                                value={currentLanguage}
                                onChange={onChangeLanguageHandler}
                                entityState={jobState}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <ToggleSwitchField
                                name="active"
                                label={branding.configuration.jobsPage.sctJobs.labelJobs}
                                onChange={makeCheckHandler(jobState, 'active')}
                                value={getElementFieldValue(jobState, "active")}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <DateField
                                name="displayStart"
                                label={branding.configuration.jobsPage.sctJobs.lblStartDisplayinOnDate}
                                validate={makeValidationHandler(job, 'displayStart')}
                                value={job.attach(Downgraded).get().displayStart}
                                onChange={(datetime: Date) => {
                                    job.displayStart.set(datetime)
                                    job.updated.set(now())
                                }}
                            />
                        </Col>
                        <Col>
                            <DateField
                                name="displayStop"
                                label={branding.configuration.jobsPage.sctJobs.lblEndDisplayinOnDate}
                                validate={makeValidationHandler(job, 'displayStop')}
                                value={job.attach(Downgraded).get().displayStop}
                                onChange={(datetime: Date) => {
                                    job.displayStop.set(datetime)
                                    job.updated.set(now())
                                }}
                            />
                        </Col>
                        <Col>
                            <DateField
                                name="updated"
                                label={branding.configuration.jobsPage.sctJobs.lblLastUpdatedDate}
                                validate={makeValidationHandler(job, 'updated')}
                                value={job.attach(Downgraded).get().updated}
                                onChange={(datetime: Date) => {
                                    job.updated.set(datetime)
                                }}
                                disabled
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextField
                                name={fieldNameByLang("title", currentLanguage)}
                                label={branding.configuration.jobsPage.sctJobs.titleEmptyJobs}
                                placeholder={branding.configuration.jobsPage.sctJobs.hntTitleJob}
                                validate={makeValidationHandlerWithLanguage(job, 'title', currentLanguage)}
                                value={getElementFieldValueWithLanguage(job, "title", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(job, 'title', currentLanguage)}
                                limit={1000}
                                required
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs="4">
                            <DateField
                                name="startdate"
                                label={branding.configuration.jobsPage.sctJobs.lblJobStartDate}
                                validate={makeValidationHandler(job, 'startdate')}
                                value={job.attach(Downgraded).get().startdate}
                                onChange={(datetime: Date) => {
                                    job.startdate.set(datetime)
                                }}
                                required
                            />
                        </Col>
                        <Col xs="4">
                            <TextField
                                name={fieldNameByLang("location", currentLanguage)}
                                label={branding.configuration.jobsPage.sctJobs.lblJobLocation}
                                placeholder={branding.configuration.jobsPage.sctJobs.hntJobLocation}
                                value={getElementFieldValueWithLanguage(job, "location", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(job, 'location', currentLanguage)}
                                limit={1000}
                            />
                        </Col>
                        <Col xs="4">
                            <RadioGroup label={branding.configuration.jobsPage.sctJobs.lblHomeoffice}>
                                <RadioField
                                    id="homeOffice"
                                    name="homeOffice"
                                    value={getElementFieldValue(job, "homeOffice") ? getElementFieldValue(job, "homeOffice") : 'na'}
                                    radioValue="na"
                                    label={branding.configuration.jobsPage.sctJobs.lblHomeofficeNA}
                                    onChange={value => job.homeOffice.set(value)}
                                />
                                <RadioField
                                    id="homeOffice"
                                    name="homeOffice"
                                    value={getElementFieldValue(job, "homeOffice")}
                                    radioValue="no"
                                    label={branding.configuration.jobsPage.sctJobs.lblHomeofficeNO}
                                    onChange={value => job.homeOffice.set(value)}
                                />
                                <RadioField
                                    id="homeOffice"
                                    name="homeOffice"
                                    value={getElementFieldValue(job, "homeOffice")}
                                    radioValue="yes"
                                    label={branding.configuration.jobsPage.sctJobs.lblHomeofficeYES}
                                    onChange={value => job.homeOffice.set(value)}
                                />
                            </RadioGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs="6">
                            <TextField
                                name={fieldNameByLang("employment", currentLanguage)}
                                label={branding.configuration.jobsPage.sctJobs.lblEmploymentType}
                                placeholder={branding.configuration.jobsPage.sctJobs.hntEmploymentType}
                                value={getElementFieldValueWithLanguage(job, "employment", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(job, 'employment', currentLanguage)}
                                limit={250}
                            />
                        </Col>
                        <Col xs="6">
                            <TextField
                                name={fieldNameByLang("careerLevel", currentLanguage)}
                                label={branding.configuration.jobsPage.sctJobs.lblEmploymentLevel}
                                placeholder={branding.configuration.jobsPage.sctJobs.hntEmploymentLevel}
                                value={getElementFieldValueWithLanguage(job, "careerLevel", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(job, 'careerLevel', currentLanguage)}
                                limit={250}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col>
                    <Row>
                        <Col>
                            <TextAreaField
                                name={fieldNameByLang("description", currentLanguage)}
                                label={branding.configuration.jobsPage.sctJobs.lblJobDescription}
                                placeholder={branding.configuration.jobsPage.sctJobs.hntJobDescription}
                                value={getElementFieldValueWithLanguage(job, "description", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(job, 'description', currentLanguage)}
                                limit={4000}
                                style={{ height: '100px' }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextAreaField
                                name={fieldNameByLang("requirements", currentLanguage)}
                                label={branding.configuration.jobsPage.sctJobs.lblJobReqirements}
                                placeholder={branding.configuration.jobsPage.sctJobs.hntJobReqirements}
                                value={getElementFieldValueWithLanguage(job, "requirements", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(job, 'requirements', currentLanguage)}
                                limit={4000}
                                style={{ height: '100px' }}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row >
            <Row>
                <Col>
                    <FilePicker
                        name="backgroundImage"
                        label={branding.configuration.jobsPage.sctJobs.lblJobBackgroundImage}
                        message={branding.configuration.jobsPage.sctJobs.lblJobsBackgroundImgMessage}
                        browseLabel={branding.configuration.jobsPage.sctJobs.btnBrowseFiles}
                        deleteLabel={branding.configuration.jobsPage.sctJobs.btnDeleteJobBackgroundImg}
                        placeholderTitle={branding.configuration.jobsPage.sctJobs.hntTitle}
                        placeholderText={branding.configuration.jobsPage.sctJobs.hntProp}
                        value={job.backgroundImageUrl.get()}
                        onChangeImage={onChangeBackgroundImage}
                        onRemoveImage={onRemoveBackgroundImage}
                    />
                </Col>
            </Row>
            {/*TODO get rid of these */}
            < br />
            <br />
            <Row>
                <Col xs={12}>
                    <Tabs>
                        {categoryVisible &&
                            <Tab eventKey="categories" title={branding.configuration.jobsPage.sctJobs.lblTabCategories}>
                                <PageContents description={branding.configuration.jobsPage.sctJobs.lblCategoriesDescription}>
                                    <CategoriesContents selectedCategoriesStates={job.categories} refCategories={refCategories} limit={serviceKeys.category} countableCategories={branding.configuration.countableCategories.jobCountableCategories} />
                                </PageContents>
                            </Tab>
                        }
                        <Tab eventKey="contacts" title={branding.configuration.jobsPage.sctJobs.lblTabContacts} tabClassName={hasErrorsAll(job.persons) ? "invalid" : ""}>
                            <PageContents description={branding.configuration.jobsPage.sctJobs.lblContactsDescription}>
                                <PersonsSection personsStates={job.persons} />
                            </PageContents>
                        </Tab>
                        <Tab eventKey="multimedia" title={branding.configuration.jobsPage.sctJobs.lblTabMultimedia} tabClassName={hasErrorsAll(job.medias) ? "invalid" : ""}>
                            <PageContents description={branding.configuration.jobsPage.sctJobs.lblMultimediaDescription}>
                                <MultimediaContents mediaStates={job.medias} index={index} multimediaLimit={branding.configuration.jobsPage.sctJobs.multimediaLimit} />
                            </PageContents>
                        </Tab>
                    </Tabs>
                </Col>
            </Row>
        </Section >
    );
}

export default React.memo(withReferenceDataState(JobsSection))
